import {Dialog, DialogDisclosure, DialogHeader, DialogProvider} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Text} from '@/components/ui/text/Text'
import {useTranslation} from '@/translations/i18n'
import {View, ViewEntity} from '../../types'
import {ViewForm} from '../view-form/ViewForm'
import {useDialogContext} from '@ariakit/react'
import {errorHandler, raise} from '@/utilities/helpers'
import {FormProvider, useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {useEditView} from '../../queries/useEditView'
import {CreateViewSchema} from '../../formSchemas'
import {AxiosError} from 'axios'
import {toast} from '@/utilities/toast/toast'

export const EditViewDialog = ({
    entity,
    view,
    id,
    trigger
}: {
    entity: ViewEntity
    view: View
    id: string
    trigger: React.ReactElement
}) => {
    const {t} = useTranslation()

    return (
        <DialogProvider>
            <DialogDisclosure render={trigger} />
            <Dialog width="400px">
                <DialogHeader>
                    <Text render="h1" size="textXl" weight={500}>
                        {t('commons:edit_view')}
                    </Text>
                </DialogHeader>
                <EditViewForm entity={entity} view={view} id={id} />
            </Dialog>
        </DialogProvider>
    )
}

const EditViewForm = ({entity, view, id}: {entity: ViewEntity; view: View; id: string}) => {
    const {t} = useTranslation()
    const {setOpen} = useDialogContext() ?? raise('Dialog not found')
    const form = useForm<CreateViewSchema>({
        defaultValues: {
            name: view.name,
            visibility: view.visibility
        },
        resolver: zodResolver(CreateViewSchema)
    })

    const editViewMutation = useEditView({
        onSuccess: () => {
            setOpen(false)
            toast.success(t('commons:view_edited'))
        },
        onError: error => {
            if (error instanceof AxiosError && error.response?.data.code == 'already-existing-name') {
                form.setError('name', {message: t('errors:view_already_exists')})
            } else {
                errorHandler(error)
            }
        }
    })

    return (
        <FormProvider {...form}>
            <ViewForm
                entity={entity}
                submitLabel={t('commons:edit_view')}
                isPending={editViewMutation.isPending}
                onSubmit={data => {
                    editViewMutation.mutate({
                        urlParams: {id},
                        payload: {
                            ...data,
                            entity,
                            body: view.body
                        }
                    })
                }}
            />
        </FormProvider>
    )
}
